import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { formatCurrency } from 'ddc-intl';
import logger from 'ws-scripts/modules/logger';
import { useFlags, useLabels, usePrefs } from 'wsm-common-data';
import useEventBus from '../hooks/useEventBus';
import usePaymentData from '../hooks/usePaymentData';
import useTrackEvent from '../hooks/useTrackEvent';
import PaymentSelectOption from './PaymentSelectOption';
import InlineDotsLoader from './InlineDotsLoader';
import DisclaimerText from './DisclaimerText';

import { setPayment } from '../features/steps/stepsSlice';
import { getLsWalletId } from '../helpers';

const PaymentSelector = () => {
	const dispatch = useDispatch();
	const [selectedId, setSelectedId] = useState(0);
	const [isLoading, hasError, paymentData] = usePaymentData();
	const [eventBus, isReady] = useEventBus();
	const sendTrackEvent = useTrackEvent();
	const flags = useFlags();
	const enablePreload =
		flags['ws-track-my-offer-preload-payment-data'] || false;
	const labels = useLabels();
	const prefs = usePrefs();
	const { showTaxesAndFeesDisclaimer } = prefs;

	// AC1/AC2 — Read prefs directly to control which payment tabs are visible.
	// Default to 'true' if the pref is not defined (zero regression).
	const showFinance = prefs.showFinance ?? 'true';
	const showLease = prefs.showLease ?? 'true';
	const showCash = prefs.showCash ?? 'true';

	const selectedPayment =
		paymentData?.length && paymentData[selectedId]?.payment;
	const selectedDealType =
		paymentData?.length &&
		paymentData[selectedId]?.paymentLabel.toLowerCase();

	// Extract taxes and fees flags from payment data
	const showTaxesAndFeesDisclaimerText = useMemo(
		() => paymentData?.some((data) => data.hasTaxesAndFees) || false,
		[paymentData]
	);

	// Component lifecycle logging
	useEffect(() => {
		logger.info('WS-TRACK-MY-OFFER: PaymentSelector mounted', {
			enablePreload,
			hasError,
			isLoading,
			paymentDataLength: paymentData?.length || 0
		});
		return () => {
			logger.warn('WS-TRACK-MY-OFFER: PaymentSelector unmounting!', {
				hasError,
				isLoading,
				paymentData: paymentData?.length || 0,
				selectedPayment,
				selectedDealType,
				enablePreload
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Log state changes
	useEffect(() => {
		logger.info('WS-TRACK-MY-OFFER: PaymentSelector state changed', {
			hasError,
			isLoading,
			paymentDataLength: paymentData?.length || 0,
			paymentData: paymentData?.map((d) => ({
				label: d.paymentLabel,
				payment: d.payment
			})),
			enablePreload
		});
	}, [hasError, isLoading, paymentData, enablePreload]);

	useEffect(() => {
		if (isReady) {
			const handleUdscalcPencilingDataUpdateRequest = (data) => {
				if (data && paymentData) {
					const paymentItemId = paymentData.findIndex(
						(item) =>
							item.paymentLabel.toLowerCase() ===
							data.dealType?.toLowerCase()
					);

					if (paymentItemId !== selectedId) {
						setSelectedId(paymentItemId);
					}
				}
			};

			eventBus.on(
				'uds:calcPencilingDataUpdate:updated',
				handleUdscalcPencilingDataUpdateRequest
			);

			return () => {
				eventBus.off(
					'uds:calcPencilingDataUpdate:updated',
					handleUdscalcPencilingDataUpdateRequest
				);
			};
		}

		// default clean up
		return () => {};
	}, [isReady, eventBus, paymentData, selectedId]);

	// Set selectedId based on the index of the object that is the dealType (isSelected)
	useEffect(() => {
		if (paymentData?.length) {
			const selectedPaymentIndex = paymentData.findIndex(
				(data) => data.isSelected
			);

			setSelectedId(
				selectedPaymentIndex !== -1 ? selectedPaymentIndex : 0
			);
		}
	}, [paymentData]);

	useEffect(() => {
		const pubsub = window?.DDC?.pubsub;
		const hasInteractedWithWallet = !!getLsWalletId();
		let paymentToPublish = null;

		if (selectedPayment && selectedDealType !== 'cash') {
			paymentToPublish = formatCurrency(selectedPayment);
		}

		if (pubsub) {
			pubsub.publish('ws-vehicle-toolbar/show-vehicle-payment', {
				payment: paymentToPublish
			});
		}
		// TODO: handle payment error
		dispatch(
			setPayment({
				complete:
					hasInteractedWithWallet &&
					!!selectedPayment &&
					!!selectedDealType,
				paymentType: selectedDealType,
				total: selectedPayment,
				enabled: true
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPayment, selectedDealType]);

	const getPlaceholders = () => {
		if (enablePreload) {
			// Build visible tabs from prefs. Only render tabs where pref !== 'false'.
			const visibleTabs = [
				{ type: 'finance', label: 'Finance', pref: showFinance },
				{ type: 'lease',   label: 'Lease',   pref: showLease   },
				{ type: 'cash',    label: 'Cash',    pref: showCash    }
			].filter(({ pref }) => pref !== 'false');

			if (visibleTabs.length === 0) {
				return null;
			}

			const total = visibleTabs.length;

			const positionClass = (i) => {
				if (total === 1) return 'border rounded selected';
				if (i === 0)
					return 'border-left rounded-left left-option selected border rounded';
				if (i === total - 1)
					return 'border-right rounded-right right-option border-top border-bottom transparent';
				return 'middle-option border-top border-bottom transparent';
			};

			return (
				<div
					className="w-100 d-flex"
					style={{
						justifyContent: 'space-evenly',
						position: 'relative'
					}}
					data-preload-container="payment-options"
					data-testid="payment-options"
				>
					{visibleTabs.map(({ type, label }, index) => (
						<div
							key={type}
							className={`position-relative payment-option-container py-3 d-flex border justify-content-center ${positionClass(index)}`}
							style={{ flex: '1 1 0px' }}
							data-payment-type={type}
						>
							<label
								htmlFor={`payment_option_${index}_input`}
								className="my-0 font-weight-normal w-100"
							>
								<div className="d-flex flex-row justify-content-center mt-1">
									<input
										type="radio"
										className="mt-0 mr-2"
										id={`payment_option_${index}_input`}
										value={label}
										name="paymentSelection"
										defaultChecked={index === 0}
										disabled
									/>
									<span className="ddc-font-size-small">{label}</span>
								</div>
								<div className="text-center">
									<span className="font-weight-bold ddc-font-size-base">
										<InlineDotsLoader />
									</span>
									{type !== 'cash' && (
										<span className="text-muted ddc-font-size-xsmall">
											{' '}/ mo
										</span>
									)}
								</div>
							</label>
						</div>
					))}
					{/* one inset-border div per gap between adjacent tabs */}
					{total > 1 &&
						Array.from({ length: total - 1 }, (_, i) => (
							<div
								key={`inset-border-${i + 1}`}
								className="inset-border"
								style={{ display: 'none' }}
								data-inset-border={i + 1}
							/>
						))}
				</div>
			);
		}

		return (
			<div
				className="skeleton-shimmer border rounded"
				style={{ height: '70px', minHeight: '70px' }}
				data-testid="payment-skeleton-shimmer"
			>
				<div className="d-flex flex-row">
					<div className="ddc-font-size-small"> </div>
				</div>
			</div>
		);
	};

	const containerStyle = {
		justifyContent: 'space-evenly'
	};

	// Handle errors based on feature flag
	if (hasError && !paymentData) {
		if (enablePreload) {
			// Enhanced behavior: Show error message instead of infinite loader
			logger.warn(
				'WS-TRACK-MY-OFFER: PaymentSelector has error, showing error state',
				{
					hasError,
					isLoading,
					paymentData,
					selectedPayment,
					selectedDealType
				}
			);
			// Show error message similar to core module's error handling
			return (
				<div className="d-flex pt-4 rounded" style={containerStyle}>
					<div
						className="w-100 text-center p-4"
						style={{
							backgroundColor: '#f8f8f8',
							border: '1px solid #ddd',
							borderRadius: '4px'
						}}
					>
						<p
							style={{
								margin: 0,
								color: '#333',
								fontSize: '16px'
							}}
						>
							Payments for this vehicle are unavailable.
						</p>
						<p
							style={{
								marginTop: '10px',
								marginBottom: 0,
								color: '#666',
								fontSize: '14px'
							}}
						>
							Please call for more information.
						</p>
					</div>
				</div>
			);
		} else {
			// Old behavior: hide widget on error
			logger.error(
				'WS-TRACK-MY-OFFER: PaymentSelector hiding due to error',
				{
					hasError,
					isLoading,
					paymentData,
					selectedPayment,
					selectedDealType
				}
			);
			return null;
		}
	}

	const onPaymentSelect = (id) => {
		// set internal selected id
		setSelectedId(id);

		// Use dealType to select current deal type
		const dealType = paymentData[id]?.paymentLabel?.toLowerCase();
		logger.info(
			`WS-TRACK-MY-OFFER -> PaymentSelect -> Deal Type: ${dealType}`
		);

		eventBus?.emit('uds:setDealType:request', {
			dealType
		});

		sendTrackEvent({
			element: 'Build Offer Toggle',
			elementCTA: paymentData[id]?.paymentLabel,
			result: 'Change deal type'
		});
	};

	const getPaymentSelectOptions = () => {
		logger.info('WS-TRACK-MY-OFFER: getPaymentSelectOptions called', {
			paymentDataLength: paymentData?.length || 0,
			paymentData: paymentData?.map((d) => ({
				label: d.paymentLabel,
				payment: d.payment,
				key: d.key
			}))
		});

		// Filter paymentData by prefs — only render tabs where pref !== 'false'
		const prefByType = { finance: showFinance, lease: showLease, cash: showCash };
		const filteredPaymentData = paymentData.filter(
			(d) => prefByType[d.paymentLabel?.toLowerCase()] !== 'false'
		);

		// Display payment options when there is at least one valid payment
		// other invalid payment options are displayed as error states
		const hasValidPayment = filteredPaymentData.some((data) => data.payment > 0);

		// If no valid payment options, return empty
		if (!hasValidPayment) {
			logger.warn(
				'WS-TRACK-MY-OFFER: No valid payment options found after filtering',
				{
					originalData: paymentData,
					filtered: filteredPaymentData,
					originalCount: paymentData?.length || 0,
					filteredCount: filteredPaymentData.length
				}
			);
			return null;
		}

		return filteredPaymentData.map((data, index) => {
			const {
				payment,
				paymentTerm,
				paymentLabel,
				hasSpecial,
				key,
				hasTaxesAndFees,
				hasError: hasPaymentError,
				firstItem,
				lastItem
			} = data;

			return (
				<PaymentSelectOption
					id={index}
					paymentAmount={payment}
					term={paymentTerm}
					paymentTypeLabel={paymentLabel}
					isSelected={selectedId === index}
					onSelect={onPaymentSelect}
					firstItem={firstItem}
					lastItem={lastItem}
					hasSpecial={hasSpecial}
					isLoading={isLoading}
					key={key}
					hasTaxesAndFees={
						hasTaxesAndFees && showTaxesAndFeesDisclaimer === 'true'
					}
					hasError={hasPaymentError}
				/>
			);
		});
	};

	if (enablePreload && (isLoading || !paymentData)) {
		logger.info('WS-TRACK-MY-OFFER: Rendering preload placeholders', {
			enablePreload,
			isLoading,
			hasPaymentData: !!paymentData
		});
		// For preload mode, we need special handling to show/hide containers
		return <div className="pt-4 rounded">{getPlaceholders()}</div>;
	}

	const paymentOptions =
		isLoading || !paymentData
			? getPlaceholders()
			: getPaymentSelectOptions();

	logger.info('WS-TRACK-MY-OFFER: PaymentSelector final render', {
		isLoading,
		hasPaymentData: !!paymentData,
		paymentDataLength: paymentData?.length || 0,
		hasPaymentOptions: !!paymentOptions,
		enablePreload
	});

	return (
		!!paymentOptions && (
			<>
				<div className="d-flex pt-4 rounded" style={containerStyle}>
					{paymentOptions}
				</div>
				{showTaxesAndFeesDisclaimerText &&
					showTaxesAndFeesDisclaimer === 'true' && (
						<DisclaimerText
							text={labels.get('DISCLAIMER_TAXES_AND_FEES')}
							key="taxes-fees-disclaimer"
						/>
					)}
			</>
		)
	);
};

export default PaymentSelector;
